// pages/admin/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  FiUsers, FiUserCheck, FiUserPlus, FiUserX, 
  FiDollarSign, FiTrendingUp, FiTrendingDown, 
  FiActivity, FiClock, FiMapPin, FiStar, FiAward,
  FiCalendar, FiBarChart2, FiPieChart
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import axios from 'axios';

function AdminDashboard() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  
  const [adminStats, setAdminStats] = useState({
    users: { total: 0, active: 0, newToday: 0, growth: 0 },
    drivers: { total: 0, online: 0, onRide: 0, offline: 0 },
    rides: { total: 0, today: 0, completed: 0, cancelled: 0 },
    revenue: { total: 0, today: 0, avgFare: 0, growth: 0 },
    shares: { price: 0, volume: 0, marketCap: 0, change: 0 },
    ratings: { avg: 0, total: 0, fiveStar: 0 },
  });
  
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentRides, setRecentRides] = useState([]);
  const [topDrivers, setTopDrivers] = useState([]);

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchAllData, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchAllData = async () => {
    setError(null);
    try {
      await Promise.all([
        fetchAdminStats(),
        fetchRecentUsers(),
        fetchRecentRides(),
        fetchTopDrivers(),
      ]);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching admin data:', error);
      setError('Failed to load admin dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminStats = async () => {
    try {
      // Fetch users
      const usersRes = await axios.get('/api/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch drivers
      const driversRes = await axios.get('/api/drivers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch rides
      const ridesRes = await axios.get('/api/rides', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch share price
      const shareRes = await axios.get('/api/share/price', {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Process data
      const users = usersRes.data.users || [];
      const drivers = driversRes.data.drivers || [];
      const rides = ridesRes.data.rides || [];
      
      const today = new Date().toISOString().split('T')[0];
      const todayRides = rides.filter(r => r.createdAt?.startsWith(today));
      const completedRides = rides.filter(r => r.status === 'completed');
      const cancelledRides = rides.filter(r => r.status === 'cancelled');
      
      const totalRevenue = completedRides.reduce((sum, r) => sum + (r.fare || 0), 0);
      const todayRevenue = todayRides
        .filter(r => r.status === 'completed')
        .reduce((sum, r) => sum + (r.fare || 0), 0);
      
      const avgFare = completedRides.length > 0 ? totalRevenue / completedRides.length : 0;
      
      const onlineDrivers = drivers.filter(d => d.status === 'online').length;
      const onRideDrivers = drivers.filter(d => d.status === 'on-ride').length;
      const offlineDrivers = drivers.filter(d => d.status === 'offline').length;

      const ratings = completedRides
        .filter(r => r.rating)
        .map(r => r.rating);
      const avgRating = ratings.length > 0 ? 
        ratings.reduce((sum, r) => sum + r, 0) / ratings.length : 0;
      const fiveStar = ratings.filter(r => r >= 4.5).length;

      const shareData = shareRes.data.data || {};
      const price = shareData.price || 100;
      const priceHistory = shareData.priceHistory || [];
      const priceChange = priceHistory.length > 1 ? 
        ((price - priceHistory[priceHistory.length - 2].price) / priceHistory[priceHistory.length - 2].price * 100) : 0;

      setAdminStats({
        users: {
          total: users.length,
          active: users.filter(u => u.isActive !== false).length,
          newToday: users.filter(u => u.createdAt?.startsWith(today)).length,
          growth: 0,
        },
        drivers: {
          total: drivers.length,
          online: onlineDrivers,
          onRide: onRideDrivers,
          offline: offlineDrivers,
        },
        rides: {
          total: rides.length,
          today: todayRides.length,
          completed: completedRides.length,
          cancelled: cancelledRides.length,
        },
        revenue: {
          total: totalRevenue,
          today: todayRevenue,
          avgFare: avgFare,
          growth: 0,
        },
        shares: {
          price: price,
          volume: shareData.tradingVolume || 0,
          marketCap: price * (shareData.totalShares || 1000),
          change: priceChange,
        },
        ratings: {
          avg: avgRating,
          total: ratings.length,
          fiveStar: fiveStar,
        },
      });
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      throw error;
    }
  };

  const fetchRecentUsers = async () => {
    try {
      const response = await axios.get('/api/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const users = response.data.users || [];
        setRecentUsers(users.slice(-5).reverse());
      }
    } catch (error) {
      setRecentUsers([]);
    }
  };

  const fetchRecentRides = async () => {
    try {
      const response = await axios.get('/api/rides?limit=10', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setRecentRides(response.data.rides || []);
      }
    } catch (error) {
      setRecentRides([]);
    }
  };

  const fetchTopDrivers = async () => {
    try {
      const response = await axios.get('/api/drivers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const drivers = response.data.drivers || [];
        const sorted = drivers
          .sort((a, b) => (b.rating || 0) - (a.rating || 0))
          .slice(0, 5);
        setTopDrivers(sorted);
      }
    } catch (error) {
      setTopDrivers([]);
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div><p>Loading admin dashboard...</p></div>;
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p style={{ color: 'var(--danger)', marginBottom: '16px' }}>{error}</p>
        <button className="btn btn-primary" onClick={fetchAllData}>🔄 Retry</button>
      </div>
    );
  }

  const { users, drivers, rides, revenue, shares, ratings } = adminStats;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <h2 style={{ fontSize: '20px' }}>📊 Admin Dashboard</h2>
        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
          Last updated: {lastUpdated.toLocaleString()}
        </span>
      </div>

      {/* Key Metrics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiUsers style={{ color: '#667eea' }} /> Total Users
          </div>
          <div className="stat-value">{users.total}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            {users.active} active • +{users.newToday} today
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiUserCheck style={{ color: '#2ecc71' }} /> Drivers
          </div>
          <div className="stat-value">{drivers.total}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            {drivers.online} online • {drivers.onRide} on ride
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiMapPin style={{ color: '#ffa502' }} /> Rides
          </div>
          <div className="stat-value">{rides.total.toLocaleString()}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            {rides.today} today • {rides.completed} completed
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiDollarSign style={{ color: '#667eea' }} /> Revenue
          </div>
          <div className="stat-value">${revenue.total.toFixed(2)}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            ${revenue.today.toFixed(2)} today • Avg ${revenue.avgFare.toFixed(2)}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiTrendingUp style={{ color: shares.change >= 0 ? '#2ecc71' : '#ff4757' }} /> Share Price
          </div>
          <div className="stat-value" style={{ color: shares.change >= 0 ? '#2ecc71' : '#ff4757' }}>
            ${shares.price.toFixed(2)}
          </div>
          <div style={{ fontSize: '12px', color: shares.change >= 0 ? '#2ecc71' : '#ff4757' }}>
            {shares.change >= 0 ? '▲' : '▼'} {Math.abs(shares.change).toFixed(2)}%
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiStar style={{ color: '#ffa502' }} /> Ratings
          </div>
          <div className="stat-value">{ratings.avg.toFixed(1)} ⭐</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            {ratings.fiveStar} five-star • {ratings.total} total
          </div>
        </div>
      </div>

      {/* Recent Activity & Top Drivers */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">🚕 Recent Rides</span>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              {recentRides.length} rides
            </span>
          </div>
          {recentRides.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>
              No recent rides found.
            </div>
          ) : (
            <div>
              {recentRides.slice(0, 5).map(ride => (
                <div key={ride.id} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  padding: '10px 0',
                  borderBottom: '1px solid var(--border-color)',
                }}>
                  <div>
                    <div style={{ fontWeight: '500' }}>
                      {ride.passengerName || 'Unknown'} → {ride.driverName || 'N/A'}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      ${(ride.fare || 0).toFixed(2)} • {(ride.distance || 0).toFixed(1)}km
                    </div>
                  </div>
                  <span className={`badge badge-${ride.status}`}>
                    {ride.status || 'unknown'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">🏆 Top Drivers</span>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              By rating
            </span>
          </div>
          {topDrivers.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>
              No drivers found.
            </div>
          ) : (
            <div>
              {topDrivers.map((driver, index) => (
                <div key={driver.id} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  padding: '8px 0',
                  borderBottom: '1px solid var(--border-color)',
                }}>
                  <span style={{ 
                    fontSize: '18px',
                    fontWeight: '700',
                    color: index === 0 ? '#ffd700' : 'var(--text-muted)',
                    width: '24px',
                  }}>
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500' }}>{driver.fullName || driver.username}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {driver.vehicleType} • {driver.totalRides || 0} rides
                    </div>
                  </div>
                  <span style={{ color: '#ffa502' }}>
                    ⭐ {driver.rating || 5.0}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Users */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">👤 Recent Users</span>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            {recentUsers.length} new users
          </span>
        </div>
        {recentUsers.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>
            No recent users found.
          </div>
        ) : (
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Joined</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentUsers.map(user => (
                  <tr key={user.id}>
                    <td><strong>{user.fullName || user.username}</strong></td>
                    <td>{user.email}</td>
                    <td><span className={`badge badge-${user.role}`}>{user.role}</span></td>
                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td>
                      <span className={`badge ${user.isActive !== false ? 'badge-active' : 'badge-inactive'}`}>
                        {user.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;